import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { LucideAngularModule } from 'lucide-angular';
import { PopupMessageComponent } from '../../../components/popup-message/popup-message.component';
import { SpinnerComponent } from '../../../components/spinner/spinner.component';
import { Icons } from '../../../../shared/helpers/icons';
import { ActivatedRoute, Router } from '@angular/router';
import { UserDataService } from '../../../../shared/services/auth/userData.service';
import { RoarAssignmentFormService } from '../../../../shared/services/roarAssignmentForm.service';
import { IRoarAssignmentForm } from '../../../../shared/models/roarAssignmentForm';
import { ActivityDetailsComponent } from './activity-details/activity-details.component';
import { ActivityOfficialSignatureComponent } from './activity-official-signature/activity-official-signature.component';
import { ActivitySubmissionComponent } from './activity-submission/activity-submission.component';
import { ToastrService } from 'ngx-toastr';

@Component({
  selector: 'roar-activity-form',
  standalone: true,
  imports: [
    CommonModule,
    LucideAngularModule,
    PopupMessageComponent,
    SpinnerComponent,
    ActivityDetailsComponent,
    ActivityOfficialSignatureComponent,
    ActivitySubmissionComponent
  ],
  templateUrl: './roar-activity-form.component.html',
  styleUrl: './roar-activity-form.component.css'
})
export class RoarActivityFormComponent implements OnInit {

  icons = Icons;
  currentBuildingId: string | null = null;

  currentCaseId: string | null = null;
  currentRoarId: string | null = null;
  currentRoarAssignmentForm: any | null = null;
  stepsConfig = [
    {
      id: 1,
      completionProperty: 'activity_section_completion',
      nextStepDependency: null
    },
    {
      id: 2,
      completionProperty: 'activity_official_signatures_section_completion',
      nextStepDependency: 'activity_section_completion'
    }
  ];
  finalStepConfig = {
      id: 3,
      completionProperty: 'roar_activity_completion',
      nextStepDependency: 'activity_official_signatures_section_completion'
  };
  steps = [1, 2];
  currentStep: number = 1;
  tooltips = ['ROAR Activity Details', 'Official Signature', 'Submission'];
  messageModal: string | null = null;
  popupMessage: string | null = null;

  loading: boolean = false;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private userDataservice: UserDataService,
    private roarAssignedFormService: RoarAssignmentFormService,
    private toastr: ToastrService
  ) {}

  
  ngOnInit(): void {
    this.currentBuildingId = this.userDataservice.getBuildingId();
    
    this.route.paramMap.subscribe((params) => {
      this.currentRoarId = params.get('roarId');
      this.currentCaseId = params.get('caseId');
    });
    
    this.loadRoarAssignmentForm();
  }

  loadRoarAssignmentForm(): void {
    if (this.currentRoarId) {
      this.loading = true;
      this.roarAssignedFormService.getRoarAssignmentFormByRoarId(this.currentRoarId).subscribe({
        next: (response) => {
          if (Object.keys(response).length === 0) {
            this.currentRoarAssignmentForm = null;
            this.loading = false;
            return
          } else {
            this.currentRoarAssignmentForm = response;
            this.loading = false;
            return
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error:', error);
          this.messageModal = 'unsuccess';
          this.popupMessage = error.message;
        }
      });
    }
  }

  handleUpdateRoarActivity(data: IRoarAssignmentForm, section: number): void {
    if (this.currentRoarId && this.currentBuildingId) {
      this.loading = true;
      const fullData = data;
      fullData.case_id = this.currentCaseId!;
      fullData.roar_id = this.currentRoarId!;
      fullData.section = section;
      fullData.building_id = Number(this.currentBuildingId);
      this.roarAssignedFormService.updateRoarActivity(fullData).subscribe({
        next: (response) => {
          this.loading = false;
          this.loadRoarAssignmentForm();
          this.toastr.success('Saved successful!', 'Success');
          this.currentStep = this.currentStep != this.finalStepConfig.id ? section + 1 : this.currentStep;
          if (section == 3 ) {
            const basePath = this.router.url.split('/')[1] || 'plans';
            this.router.navigate([`/${basePath}/`]);
          }
        },
        error: (error) => {
          this.loading = false;
          console.error('Error updating Roar Activity:', error);
          this.toastr.error(error.message, 'Error');
        }
      });
    }
  }

  onBack(): void {
    this.router.navigate(['/plans/']);
  }
  
  // move to next section
  goToStep(stepId: number): void {
    if (this.canMoveToStep(stepId)) {
      this.currentStep = stepId;
    } else {
      console.error('You must complete the required steps before proceeding.');
    }
  }
  // check if they already completed the required step before allowing them to move to the next step
  canMoveToStep(stepId: number): boolean {
    let stepConfig;
    if (stepId === this.finalStepConfig.id) {
      stepConfig = this.finalStepConfig; // Handle the final step separately
    } else {
      stepConfig = this.stepsConfig.find(step => step.id === stepId);
    }
  
    if (!stepConfig) {
      console.error('Step configuration not found for step', stepId);
      return false;
    }
  
    if (stepConfig.nextStepDependency && this.currentRoarAssignmentForm) {
      return this.currentRoarAssignmentForm?.[stepConfig.nextStepDependency] === 1;
    }
  
    return true;
  }

  
  resetMessageModal(): void {
    this.messageModal = null;
    this.popupMessage = null;
  }

}
